import { useCurrentUserStaff } from "@/hooks/useCurrentUserStaff";
import { useSuperAdminGate } from "@/hooks/useSuperAdminGate";
import { useOwnerGate } from "@/hooks/cockpit/useOwnerGate";
import { useHrAdminGate } from "@/hooks/cockpit/useHrAdminGate";
import type { RouteGate } from "@/routes/types";
import { useCoachingAccess } from "@/hooks/coaching/useCoachingAccess";

/**
 * useRouteGate — the ONE evaluator for a route registry `gate`, built only
 * on the staff_roles world (useCurrentUserStaff) + the owner gate. This is
 * what both sidebar nav visibility (Layout.tsx) and, where a page enforces
 * its own guard, the page itself should read — so a gate mismatch between
 * "the link is shown" and "the page lets you in" becomes structurally
 * impossible. RLS remains the data authority; this only controls what
 * renders client-side.
 *
 * Mapping:
 *  - 'public' / 'authenticated' — always allowed (these render inside the
 *    already-authenticated app shell; there's nothing further to gate).
 *  - 'manager' — the exact predicate GamesConsole/GamesLaunch use today:
 *    currentUserStaff.role is 'admin' (super_admin) or 'manager'.
 *  - 'academy' — admin, manager, office_manager or coach.
 *  - 'billingManager' — mirrors app_can_manage_billing() (admin OR
 *    office_manager/super_admin): isSuperAdmin (owner OR staff_roles
 *    super_admin) OR currentUserStaff.role === 'office_manager'.
 *    Deliberately NOT satisfied by plain 'manager' — see audit-hardening E1
 *    (BillingPayments.tsx's own useCanManageBilling RPC call is the real
 *    enforcement; this only controls what renders/is discoverable).
 *  - 'payablesManager' — mirrors app_can_manage_payables() (admin OR
 *    office_manager/super_admin), same predicate shape as 'billingManager' —
 *    isSuperAdmin OR currentUserStaff.role === 'office_manager'. Not used by
 *    /payables/submit (that route is 'authenticated' — submission is open to
 *    all staff); reserved for later payables admin surfaces (review inbox,
 *    match workbench). Real RPC-backed enforcement on those pages is
 *    useCanManagePayables (src/hooks/payables/useCanManagePayables.ts),
 *    mirroring how BillingPayments.tsx layers useCanManageBilling on top of
 *    this client-side nav gate.
 *  - 'moneyFlows' — mirrors app_can_view_money_flows() (AP Phase 5 Task A1):
 *    isSuperAdmin OR currentUserStaff.role === 'accountant'. Narrower than
 *    'payablesManager' — office_manager is deliberately excluded (Tier 2
 *    keeps the invoice world, never the bank feed/imports/matching). Gates
 *    /payables/bank. Real RPC-backed enforcement is useCanViewMoneyFlows
 *    (src/hooks/payables/useCanViewMoneyFlows.ts), same layering as
 *    payablesManager/useCanManagePayables.
 *  - 'admin' — useSuperAdminGate.isSuperAdmin (owner OR staff_roles
 *    super_admin), same gate Reports.tsx uses.
 *  - 'owner' — useOwnerGate (Artem, matched by email).
 *  - 'ownerOrAccountant' — 'owner' OR staff_roles role 'accountant' (the
 *    bookkeeper). Gates /cockpit/financials only (owner ruling 2026-10-07).
 *  - 'hrAdmin' — useHrAdminGate (Artem + Antonella, matched by email).
 *  - 'privileged' — any resolved staff_roles role other than plain 'staff'
 *    (admin, manager, office_manager, coach, ...). A blocklist, not an
 *    allowlist — deliberately not "admin or manager" (which would exclude
 *    coach/office_manager) and not itemized per-role, so a future role
 *    passes by default. Used to gate the staff directory/profile,
 *    compliance and hub surfaces to everyone except plain teachers (RLS
 *    phase2-final, Task A2) — RLS on staff_master is the real boundary.
 *  - 'internCoordinator' — role is 'admin', 'manager', 'office_manager',
 *    'coach' or 'intern_manager'. The intern-coordinator seat (conductor
 *    decision 2026-09-14, after Artem named Marilia — a coach — as the
 *    coordinator): the same seat carries the intern_stipend_months RLS
 *    policies and the intern_profile_update RPC gate. Those gate on
 *    app_is_manager(), live-defined as
 *    role IN ('manager','super_admin','office_manager') — so office_manager
 *    already passes in the DB and this mirrors it (review finding F4, accepted
 *    2026-09-14); leaving it out only hid a tab from someone RLS lets write.
 *    super_admin needs no branch here: useCurrentUserStaff resolves it to
 *    'admin'. Still an allowlist and NOT 'privileged' — 'accountant' has no
 *    intern pay role.
 *  - 'hiring' — role is 'admin', 'manager', or 'coach'. Mirrors the
 *    candidates/candidate_documents RLS policies (cand_mgr_read/write,
 *    cdoc_mgr_read/write: app_is_admin() or app_is_manager() or
 *    app_is_coach()) — deliberately an allowlist, NOT 'privileged':
 *    intern_manager has no recruitment access (RLS phase2-final,
 *    intern_manager task).
 *  - 'managerOrHrAdmin' — the 'manager' predicate OR useHrAdminGate.
 *    Composite: kept even though both hrAdmin emails hold super_admin in prod
 *    (probed 2026-07-27) — belt-and-suspenders for the email-gated path.
 *    Used where a surface should open to real managers while keeping
 *    existing hrAdmin-email access intact.
 *
 * `isError`/`error` surface a failed identity lookup (useCurrentUserStaff
 * throwing) on every gate, regardless of which underlying hook the gate's
 * `allowed` predicate reads — react-query dedupes the `currentUserStaff`
 * query by key, so this instance's error state matches whatever any other
 * consumer (including useSuperAdminGate, which reads the same query
 * internally) sees. This is purely additive: `allowed`/`isLoading` keep
 * their existing fail-closed semantics unchanged (an errored identity query
 * still resolves `allowed: false`).
 */
/** The 'privileged' predicate, shared with in-page (tab-level) gates. */
export function isPrivilegedRole(role: string | null | undefined): boolean {
  return !!role && role !== "staff";
}

export function useRouteGate(
  gate: RouteGate,
): { allowed: boolean; isLoading: boolean; isError: boolean; error: unknown; role?: string } {
  const { currentUserStaff, isLoading: staffLoading, isError, error } = useCurrentUserStaff();
  const { isSuperAdmin, isLoading: superAdminLoading } = useSuperAdminGate();
  const { isOwner, isLoading: ownerLoading } = useOwnerGate();
  const { isHrAdmin, isLoading: hrAdminLoading } = useHrAdminGate();
  const coachingAccess = useCoachingAccess(gate === "coaching");

  switch (gate) {
    case "public":
    case "authenticated":
      return { allowed: true, isLoading: false, isError: false, error: null };
    case "manager": {
      const isManager = currentUserStaff?.role === "admin" || currentUserStaff?.role === "manager";
      return { allowed: isManager, isLoading: staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "academy": {
      const allowed = ["admin", "manager", "office_manager", "coach"].includes(currentUserStaff?.role ?? "");
      return { allowed, isLoading: staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "internCoordinator": {
      const isInternCoordinator =
        currentUserStaff?.role === "admin" ||
        currentUserStaff?.role === "manager" ||
        currentUserStaff?.role === "office_manager" ||
        currentUserStaff?.role === "coach" ||
        currentUserStaff?.role === "intern_manager";
      return { allowed: isInternCoordinator, isLoading: staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "billingManager": {
      const isBillingManager =
        isSuperAdmin || currentUserStaff?.role === "office_manager" || currentUserStaff?.role === "accountant";
      return { allowed: isBillingManager, isLoading: staffLoading || superAdminLoading, isError, error, role: currentUserStaff?.role };
    }
    case "payablesManager": {
      const isPayablesManager =
        isSuperAdmin || currentUserStaff?.role === "office_manager" || currentUserStaff?.role === "accountant";
      return { allowed: isPayablesManager, isLoading: staffLoading || superAdminLoading, isError, error, role: currentUserStaff?.role };
    }
    case "moneyFlows": {
      const isMoneyFlows = isSuperAdmin || currentUserStaff?.role === "accountant";
      return { allowed: isMoneyFlows, isLoading: staffLoading || superAdminLoading, isError, error, role: currentUserStaff?.role };
    }
    case "admin":
      return { allowed: isSuperAdmin, isLoading: superAdminLoading, isError, error, role: currentUserStaff?.role };
    case "owner":
      return { allowed: isOwner, isLoading: ownerLoading, isError: false, error: null };
    case "ownerOrAccountant": {
      const allowed = isOwner || currentUserStaff?.role === "accountant";
      return { allowed, isLoading: ownerLoading || staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "hrAdmin":
      return { allowed: isHrAdmin, isLoading: hrAdminLoading, isError: false, error: null };
    case "privileged": {
      const isPrivileged = isPrivilegedRole(currentUserStaff?.role);
      return { allowed: isPrivileged, isLoading: staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "hiring": {
      const isHiring =
        currentUserStaff?.role === "admin" ||
        currentUserStaff?.role === "manager" ||
        currentUserStaff?.role === "coach";
      return { allowed: isHiring, isLoading: staffLoading, isError, error, role: currentUserStaff?.role };
    }
    case "coaching": {
      const allowed = !!coachingAccess.data && (
        coachingAccess.data.canReadAll || coachingAccess.data.locationCodes.length > 0
      );
      return {
        allowed,
        isLoading: coachingAccess.isLoading,
        isError: coachingAccess.isError,
        error: coachingAccess.error,
      };
    }
    case "managerOrHrAdmin": {
      const isManager = currentUserStaff?.role === "admin" || currentUserStaff?.role === "manager";
      return { allowed: isManager || isHrAdmin, isLoading: staffLoading || hrAdminLoading, isError, error, role: currentUserStaff?.role };
    }
  }
}
